/**
 * Page ExerciseEdit — édition d'un exercice existant.
 * Route : /dashboard/seances/insightlab/:id/edit
 *
 * Charge l'exo, vérifie ownership (sinon redirige vers le détail),
 * affiche ExerciseForm en mode pré-rempli.
 */
import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, AlertCircle, Loader2 } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import ExerciseForm from '../components/ProcedeForm'
import { useAuth } from '../context/AuthContext'
import { useProcede, useUpdateProcede } from '../hooks/useProcedes'
import { T } from '../theme'

export default function ExerciseEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: exo, loading, error } = useProcede(id)
  const { mutate: doUpdate, loading: saving, error: saveError, fieldErrors } = useUpdateProcede()

  // Si l'user n'est pas owner ou si c'est un template système, on redirige
  // vers la page de détail (read-only). On le fait dans un effet une fois
  // l'exo chargé.
  useEffect(() => {
    if (!exo) return
    const isOwner = exo.user_id === user?.id
    const isSystem = exo.is_template && exo.user_id === null
    if (!isOwner || isSystem) {
      navigate(`/dashboard/seances/insightlab/${exo.id}`, { replace: true })
    }
  }, [exo, user, navigate])

  if (loading) return (
    <DashboardLayout>
      <Center><Loader2 size={28} color={T.gold} className="spin" /></Center>
    </DashboardLayout>
  )

  if (error || !exo) return (
    <DashboardLayout>
      <Center>
        <AlertCircle size={32} color={T.red} />
        <p style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, margin: '12px 0' }}>
          {error || 'Exercice introuvable.'}
        </p>
      </Center>
    </DashboardLayout>
  )

  const handleSubmit = async (payload) => {
    try {
      await doUpdate(exo.id, payload)
      navigate(`/dashboard/seances/insightlab/${exo.id}`)
    } catch {
      // Erreur affichée via fieldErrors / saveError dans le form
    }
  }

  return (
    <DashboardLayout>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
      <div style={{ padding: '20px 24px 60px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Link
            to={`/dashboard/seances/insightlab/${exo.id}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: T.ink, textDecoration: 'none',
              fontFamily: T.mono, fontSize: 11, letterSpacing: '.12em',
              textTransform: 'uppercase', fontWeight: 700,
            }}
          >
            <ArrowLeft size={13} /> Retour
          </Link>
          <h1 style={{
            fontFamily: T.display, fontSize: 26, color: T.ink,
            textTransform: 'uppercase', letterSpacing: '.01em',
            margin: 0,
          }}>
            Modifier le procédé
          </h1>
        </div>

        <ExerciseForm
          initial={exo}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/dashboard/seances/insightlab/${exo.id}`)}
          isLoading={saving}
          errors={fieldErrors}
          serverError={saveError}
          submitLabel="Enregistrer les modifications"
        />
      </div>
    </DashboardLayout>
  )
}

function Center({ children }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 60, textAlign: 'center',
    }}>{children}</div>
  )
}
